import type {RepositoryPickerRepository} from '@github-ui/issues-repository-picker'
import {useEffect} from 'react'

import type {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import type {CreateIssueFragment} from './../create-issue-fragment-types'

type IssueCreateConfig = ReturnType<typeof useIssueCreateConfigContext>

type UseDefaultRepositorySelectionProps = {
  repository: RepositoryPickerRepository | undefined
  /** The current repository fragment, when one is already resolved for this surface. */
  data: CreateIssueFragment | null
  isLoadingRepositoryData: boolean
  topRepositories: RepositoryPickerRepository[] | null | 'unset'
  optionConfig: IssueCreateConfig['optionConfig']
  onRepositorySelected: (repository: RepositoryPickerRepository | undefined) => void
}

/**
 * Resolves the default repository to select when the surface opens without one already chosen:
 * the already-resolved current repository (`data`), otherwise the top repository when no scoped
 * owner drives the picker or the header switcher is shown.
 */
export function useDefaultRepositorySelection({
  repository,
  data,
  isLoadingRepositoryData,
  topRepositories,
  optionConfig,
  onRepositorySelected,
}: UseDefaultRepositorySelectionProps) {
  useEffect(() => {
    if (repository !== undefined) return

    if (data) {
      onRepositorySelected(data)
      return
    }

    if (isLoadingRepositoryData || topRepositories === 'unset' || !topRepositories) {
      return
    }

    const fallbackRepository = topRepositories[0]
    if (fallbackRepository && (!optionConfig.scopedRepositoryOwner || optionConfig.showOrgRepositoryPicker)) {
      onRepositorySelected(fallbackRepository)
    }
  }, [data, isLoadingRepositoryData, onRepositorySelected, optionConfig, repository, topRepositories])
}
